import React, { useState, useEffect, useCallback } from 'react';
import { adminServices } from '../services/adminServices';
import { useUser } from '../UserContext';
import { countryEndonyms } from '../locales';
import { newShowcaseEntryId } from '../utils/showcaseLevels';
import {
  uploadShowcaseLogo,
  deleteShowcaseLogo,
} from '../services/publicShowcaseService';

const emptyEntry = () => ({
  id: '',
  name: '',
  country: '',
  city: '',
  level: 'flagship',
  logoUrl: null,
  storagePath: null,
  sortOrder: 0,
});

const AdminPublicShowcase = () => {
  const { user, permissions } = useUser();
  const adminName = user?.displayName || user?.email || 'IFIP Staff';
  const canEdit = !!permissions.canManagePublicShowcase;

  const [entries, setEntries] = useState([]);
  const [lastPublishedAt, setLastPublishedAt] = useState(null);
  const [draft, setDraft] = useState(emptyEntry());
  const [editingId, setEditingId] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isRebuildingAccredited, setIsRebuildingAccredited] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFeedback(null);
    try {
      const data = await adminServices.getShowcaseDraft();
      setEntries(data.entries || []);
      setLastPublishedAt(data.lastPublishedAt || null);
    } catch (err) {
      console.error('getShowcaseDraft', err);
      setFeedback({
        type: 'error',
        message:
          err?.code === 'permission-denied'
            ? 'Cannot load Flagship Schools (Firestore permission). Deploy updated rules or sign in as approved staff.'
            : err?.message || 'Failed to load Flagship Schools.',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    return () => {
      if (logoPreview && logoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  const persistEntries = async (nextEntries) => {
    setIsSaving(true);
    setFeedback(null);
    try {
      await adminServices.saveShowcaseDraft(nextEntries, adminName);
      setEntries(nextEntries);
      setFeedback({ type: 'success', message: 'Flagship Schools draft saved.' });
    } catch (e) {
      setFeedback({ type: 'error', message: e?.message || 'Save failed.' });
      throw e;
    } finally {
      setIsSaving(false);
    }
  };

  const resetForm = () => {
    setDraft(emptyEntry());
    setEditingId(null);
    setLogoFile(null);
    if (logoPreview && logoPreview.startsWith('blob:')) URL.revokeObjectURL(logoPreview);
    setLogoPreview(null);
  };

  const startAdd = () => {
    resetForm();
    const id = newShowcaseEntryId();
    setDraft({
      ...emptyEntry(),
      id,
      sortOrder: entries.length,
    });
    setEditingId('__new__');
  };

  const startEdit = (entry) => {
    setDraft({ ...entry });
    setEditingId(entry.id);
    setLogoFile(null);
    setLogoPreview(entry.logoUrl || null);
  };

  const handleLogoPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setFeedback({ type: 'error', message: 'Please choose an image file.' });
      return;
    }
    setLogoFile(file);
    if (logoPreview && logoPreview.startsWith('blob:')) URL.revokeObjectURL(logoPreview);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleSaveEntry = async () => {
    const name = String(draft.name || '').trim();
    const country = String(draft.country || '').trim();
    if (!name || !country) {
      setFeedback({ type: 'error', message: 'School name and country are required.' });
      return;
    }
    const id = draft.id || newShowcaseEntryId();
    let logoUrl = draft.logoUrl || null;
    let storagePath = draft.storagePath || null;

    setIsSaving(true);
    setFeedback(null);
    try {
      if (logoFile) {
        const uploaded = await uploadShowcaseLogo(id, logoFile);
        if (storagePath && storagePath !== uploaded.storagePath) {
          await deleteShowcaseLogo(storagePath);
        }
        logoUrl = uploaded.logoUrl;
        storagePath = uploaded.storagePath;
      }

      const entry = {
        id,
        name,
        country,
        city: String(draft.city || '').trim(),
        level: 'flagship',
        logoUrl,
        storagePath,
        sortOrder: Number.isFinite(Number(draft.sortOrder)) ? Number(draft.sortOrder) : entries.length,
      };

      let next;
      const idx = entries.findIndex((e) => e.id === id);
      if (idx >= 0) {
        next = entries.slice();
        next[idx] = entry;
      } else {
        next = [...entries, entry];
      }
      await persistEntries(next);
      resetForm();
    } catch (e) {
      setFeedback({ type: 'error', message: e?.message || 'Could not save entry.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`Remove "${entry.name}" from Flagship Schools?`)) return;
    setIsSaving(true);
    try {
      if (entry.storagePath) await deleteShowcaseLogo(entry.storagePath);
      const next = entries.filter((e) => e.id !== entry.id);
      await persistEntries(next);
      if (editingId === entry.id) resetForm();
    } catch (e) {
      setFeedback({ type: 'error', message: e?.message || 'Delete failed.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleRebuildAccredited = async () => {
    if (
      !window.confirm(
        'Rebuild the public accredited schools feed from the registry? This updates public/schoolShowcase.accreditedSchools[] (all finalised active tiers: Foundation, Certified, Global) and preserves Flagship Schools.'
      )
    ) {
      return;
    }
    setIsRebuildingAccredited(true);
    setFeedback(null);
    try {
      const result = await adminServices.rebuildAccreditedPublicShowcase({ dryRun: false });
      setFeedback({
        type: 'success',
        message: `Accredited feed rebuilt (${result.accreditedCount ?? 0} school(s)). Verify on ifip.group/accreditation/schools/.`,
      });
    } catch (e) {
      setFeedback({ type: 'error', message: e?.message || 'Accredited feed rebuild failed.' });
    } finally {
      setIsRebuildingAccredited(false);
    }
  };

  const handlePublish = async () => {
    if (!entries.length) {
      setFeedback({ type: 'error', message: 'Add at least one Flagship School before publishing.' });
      return;
    }
    if (!window.confirm(`Publish ${entries.length} Flagship School(s) to ifip.group?`)) return;
    setIsPublishing(true);
    setFeedback(null);
    try {
      const { schoolCount } = await adminServices.publishSchoolShowcase(adminName);
      await load();
      setFeedback({
        type: 'success',
        message: `Published ${schoolCount} Flagship School(s) to the accreditation website.`,
      });
    } catch (e) {
      setFeedback({ type: 'error', message: e?.message || 'Publish failed.' });
    } finally {
      setIsPublishing(false);
    }
  };

  const sortedEntries = [...entries].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  if (loading) {
    return <p style={{ opacity: 0.6, fontSize: '0.85rem' }}>Loading Flagship Schools…</p>;
  }

  return (
    <div className="admin-pricing-view fade-in">
      <header style={{ marginBottom: '24px' }}>
        <h2 style={{ color: 'white', margin: '0 0 8px 0' }}>Flagship Schools</h2>
        <p style={{ fontSize: '0.8rem', color: '#94a3b8', margin: 0, maxWidth: '720px', lineHeight: 1.5 }}>
          Add Flagship Schools here to appear on the ifip.group accreditation website.
        </p>
        {lastPublishedAt && (
          <p style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '8px' }}>
            Last published:{' '}
            {lastPublishedAt?.toDate
              ? lastPublishedAt.toDate().toLocaleString()
              : String(lastPublishedAt)}
          </p>
        )}
      </header>

      {feedback && (
        <div
          style={{
            marginBottom: '16px',
            padding: '12px 16px',
            borderRadius: '8px',
            background: feedback.type === 'error' ? 'rgba(209,29,122,0.15)' : 'rgba(19,138,138,0.2)',
            color: feedback.type === 'error' ? '#f9a8d4' : '#99f6e4',
            fontSize: '0.85rem',
          }}
        >
          {feedback.message}
        </div>
      )}

      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '24px' }}>
        {canEdit && (
          <>
            <button
              type="button"
              className="admin-btn-primary"
              onClick={startAdd}
              disabled={isSaving || isPublishing || editingId === '__new__'}
            >
              Add Flagship School
            </button>
            <button
              type="button"
              className="admin-btn-primary"
              onClick={handlePublish}
              disabled={isSaving || isPublishing || isRebuildingAccredited || !entries.length}
              style={{ background: '#0d9488' }}
            >
              {isPublishing ? 'Publishing…' : 'Publish to ifip.group'}
            </button>
            <button
              type="button"
              className="admin-btn-primary"
              onClick={handleRebuildAccredited}
              disabled={isSaving || isPublishing || isRebuildingAccredited}
              style={{ background: '#334155' }}
              title="Rebuild public/schoolShowcase.accreditedSchools[] from finalised Global schools (preserves Flagship Schools)."
            >
              {isRebuildingAccredited ? 'Rebuilding accredited feed…' : 'Rebuild accredited feed'}
            </button>
          </>
        )}
      </div>

      {(editingId === '__new__' || (editingId && editingId !== '__new__')) && canEdit && (
        <section className="pricing-section" style={{ marginBottom: '28px' }}>
          <h3 style={{ color: 'white', margin: '0 0 16px 0' }}>
            {editingId === '__new__' ? 'Add Flagship School' : 'Edit Flagship School'}
          </h3>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '16px',
              maxWidth: '900px',
            }}
          >
            <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '0.75rem', color: '#94a3b8' }}>
              School name *
              <input
                className="pricing-input"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '0.75rem', color: '#94a3b8' }}>
              Country *
              <select
                className="pricing-input"
                value={draft.country}
                onChange={(e) => setDraft({ ...draft, country: e.target.value })}
                required
                style={{
                  background: '#0f172a',
                  border: '1px solid #334155',
                  color: 'white',
                  borderRadius: '4px',
                  padding: '8px 10px',
                  width: '100%',
                }}
              >
                <option value="" style={{ color: 'black' }}>
                  Select country...
                </option>
                {countryEndonyms.map((country) => (
                  <option key={country} value={country} style={{ color: 'black' }}>
                    {country}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '0.75rem', color: '#94a3b8' }}>
              City
              <input
                className="pricing-input"
                value={draft.city}
                onChange={(e) => setDraft({ ...draft, city: e.target.value })}
              />
            </label>
            
            <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '0.75rem', color: '#94a3b8' }}>
              Logo
              <input type="file" accept="image/*" onChange={handleLogoPick} />
            </label>
          </div>
          {(logoPreview || draft.logoUrl) && (
            <div style={{ marginTop: '16px' }}>
              <img
                src={logoPreview || draft.logoUrl}
                alt=""
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '2px solid rgba(255,255,255,0.2)',
                }}
              />
            </div>
          )}
          <div style={{ marginTop: '16px', display: 'flex', gap: '10px' }}>
            <button
              type="button"
              className="admin-btn-primary"
              onClick={handleSaveEntry}
              disabled={isSaving || isPublishing}
            >
              {isSaving ? 'Saving…' : 'Save to draft'}
            </button>
            <button
              type="button"
              className="admin-btn-primary"
              onClick={resetForm}
              disabled={isSaving}
              style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.35)' }}
            >
              Cancel
            </button>
          </div>
        </section>
      )}

      <section className="pricing-section">
        <h3 style={{ color: 'white', margin: '0 0 12px 0' }}>Flagship Schools ({sortedEntries.length})</h3>
        <table className="pricing-table">
          <thead>
            <tr>
              <th>Logo</th>
              <th>Name</th>
              <th>Country</th>
              <th>City</th>
              {canEdit && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {sortedEntries.length === 0 ? (
              <tr>
                <td colSpan={canEdit ? 6 : 5} style={{ opacity: 0.6, padding: '20px' }}>
                  No Flagship Schools yet. Add schools above, then publish to ifip.group.
                </td>
              </tr>
            ) : (
              sortedEntries.map((entry) => (
                <tr key={entry.id}>
                  <td>
                    {entry.logoUrl ? (
                      <img
                        src={entry.logoUrl}
                        alt=""
                        style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }}
                      />
                    ) : (
                      <span
                        style={{
                          display: 'inline-flex',
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          background: 'rgba(19,138,138,0.35)',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.7rem',
                          fontWeight: 'bold',
                          color: '#fff',
                        }}
                      >
                        {(entry.name || '?').slice(0, 2).toUpperCase()}
                      </span>
                    )}
                  </td>
                  <td>{entry.name}</td>
                  <td>{entry.country}</td>
                  <td>{entry.city || '—'}</td>
                  {canEdit && (
                    <td>
                      <button
                        type="button"
                        style={{ marginRight: '8px', cursor: 'pointer' }}
                        onClick={() => startEdit(entry)}
                        disabled={isSaving || isPublishing}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        style={{ cursor: 'pointer', color: '#f9a8d4' }}
                        onClick={() => handleDelete(entry)}
                        disabled={isSaving || isPublishing}
                      >
                        Delete
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
};

export default AdminPublicShowcase;
